import React from 'react';
import { cn } from '@/lib/utils';
import { AuthBackdrop } from './AuthBackdrop';

// ---------------------------------------------------------------------------
// The shared sign-in shell (web app v4 plan, Phase 3): every page you see
// before you're in the app, and the consent pages the native apps and Claude
// open, sit in it. One near-black surface (--ink) with AuthBackdrop's glow,
// in the dark theme's tokens whatever the app's theme is, so the funnel reads
// as one place. The pages' fields, buttons and words take their classes from
// here instead of each keeping its own copy.
// ---------------------------------------------------------------------------

/** Text inputs: a hairline field that lights up in the brand blue on focus. */
export const AUTH_FIELD_CLASS =
  'h-12 rounded-[11px] border border-foreground/10 bg-foreground/5 px-[15px] py-[13px] text-base text-foreground placeholder:text-foreground/40 focus-visible:border-primary focus-visible:ring-[3px] focus-visible:ring-primary/25';

/** The one main action: near-white on the dark page, ink label. */
export const AUTH_PRIMARY_BUTTON =
  'h-12 w-full rounded-[11px] border-0 bg-foreground text-base font-semibold text-ink transition-transform duration-150 hover:-translate-y-px hover:bg-foreground hover:shadow-[0_10px_32px_hsl(var(--primary)/0.22)] disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink';

/** Another way in (Microsoft, Apple): outlined. */
export const AUTH_OUTLINE_BUTTON =
  'h-11 w-full rounded-[11px] border border-foreground/10 bg-transparent text-foreground normal-case hover:border-foreground/25 hover:bg-foreground/5';

/** The blue alternative (passkey, email link). */
export const AUTH_ACCENT_BUTTON =
  'h-11 w-full rounded-[11px] border border-primary/30 bg-transparent text-primary normal-case hover:border-primary hover:bg-primary/10';

export const AUTH_LABEL_CLASS = 'text-xs tracking-[0.02em] text-muted-foreground';

/** The page's h1: light serif, smaller on a phone so the form clears the keyboard. */
export const AUTH_TITLE_CLASS = 'font-serif text-2xl font-light leading-tight tracking-[-0.01em] text-foreground sm:text-3xl';

export const AUTH_SUBTITLE_CLASS = 'text-sm font-light tracking-wide text-muted-foreground sm:text-base';

/** Small links under the form ("Forgot password", "14 day free trial"). */
export const AUTH_FOOTER_LINK_CLASS = 'text-muted-foreground hover:text-foreground';

/** The quiet line at the very bottom. */
export const AUTH_FOOTNOTE_CLASS = 'text-xs tracking-wide text-muted-foreground/70';

const AUTH_WIDTH = {
  /** One column of fields. */
  form: 'max-w-[392px]',
  /** Fields with plan cards beside their prices (sign-up). */
  plans: 'max-w-[440px]',
  /** A consent screen or a page of text. */
  wide: 'max-w-[560px]',
} as const;

export function AuthShell({ children, leaving = false, width = 'form', className, contentClassName }: {
  children: React.ReactNode;
  /** Fade the page to ink before handing over to the app, so it doesn't flash. */
  leaving?: boolean;
  width?: keyof typeof AUTH_WIDTH;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <div
      data-auth-shell=""
      // overflow-y-auto, not hidden: when a phone's keyboard pushes the
      // viewport up, the fields can still scroll into view (the iOS app's
      // sign-in sheet clipped them).
      className={cn(
        'dark relative flex min-h-screen items-center justify-center overflow-x-hidden overflow-y-auto bg-ink py-6 text-foreground sm:py-0',
        className,
      )}
    >
      <AuthBackdrop />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 z-[60] bg-ink transition-opacity duration-200 ease-out',
          leaving ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div className={cn('relative z-[2] mx-4 flex w-full flex-col p-8', AUTH_WIDTH[width], contentClassName)}>
        {children}
      </div>
    </div>
  );
}

export default AuthShell;
